import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileSpreadsheet, 
  ArrowLeft,
  Users,
  Search,
  BarChart3,
  TrendingUp,
  Loader2,
  RefreshCw,
  LogIn,
  FileText,
  DollarSign,
  Clock,
  Upload,
  Percent,
  Calendar,
  ExternalLink
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Stats
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Users
  const [users, setUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loggingInAs, setLoggingInAs] = useState(null);

  useEffect(() => {
    if (!authLoading && user) {
      fetchDashboardData();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const [statsRes, usersRes] = await Promise.all([
        api.get('/api/admin/stats/dashboard'),
        api.get('/api/admin/users')
      ]);
      setDashboardStats(statsRes.data);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLoginAsUser = async (targetUser) => {
    setLoggingInAs(targetUser.user_id);
    try {
      const response = await api.post(`/api/admin/login-as-user/${targetUser.user_id}`);
      toast.success(`Now logged in as ${targetUser.email}`);
      
      // Store original admin session info
      localStorage.setItem('admin_impersonation', JSON.stringify({
        original_admin: user.email,
        impersonating: targetUser.email
      }));
      
      // Refresh user context and redirect
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as user');
    } finally {
      setLoggingInAs(null);
    }
  };

  // Blog-related functions removed


  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-4">You don&apos;t have permission to view this page.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-slate-900">CRM Data Cleaner</span>
              </Link>
              <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-1 rounded">ADMIN</span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={fetchDashboardData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Admin Dashboard</h1>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/admin/lead-gen')} data-testid="lead-gen-link">
              <Users className="w-4 h-4 mr-2" />
              Lead Gen
              <ExternalLink className="w-3 h-3 ml-2" />
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/blog-manager')} data-testid="blog-manager-link">
              <FileText className="w-4 h-4 mr-2" />
              Blog Manager
              <ExternalLink className="w-3 h-3 ml-2" />
            </Button>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="overview">
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {dashboardStats && (
              <div className="space-y-8">
                {/* Revenue Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingUp className="w-5 h-5 text-emerald-100" />
                      <span className="text-sm text-emerald-100">MRR</span>
                    </div>
                    <p className="text-3xl font-bold">${dashboardStats.overview.mrr?.toLocaleString() || 0}</p>
                    <p className="text-sm text-emerald-100">Monthly Recurring</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-6 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <DollarSign className="w-5 h-5 text-indigo-100" />
                      <span className="text-sm text-indigo-100">ARR</span>
                    </div>
                    <p className="text-3xl font-bold">${dashboardStats.overview.arr?.toLocaleString() || 0}</p>
                    <p className="text-sm text-indigo-100">Annual Recurring</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-white">
                    <div className="flex items-center gap-3 mb-2">
                      <DollarSign className="w-5 h-5 text-amber-100" />
                      <span className="text-sm text-amber-100">Credit Revenue</span>
                    </div>
                    <p className="text-3xl font-bold">${dashboardStats.overview.total_credit_revenue?.toLocaleString() || 0}</p>
                    <p className="text-sm text-amber-100">One-time purchases</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Users className="w-5 h-5 text-indigo-600" />
                      <span className="text-sm text-slate-600">Total Users</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{dashboardStats.overview.total_users.toLocaleString()}</p>
                    <p className="text-sm text-emerald-600">+{dashboardStats.overview.new_users_today} today</p>
                  </div>
                </div>

                {/* Usage Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Upload className="w-5 h-5 text-blue-600" />
                      <span className="text-sm text-slate-600">Files Processed</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{dashboardStats.overview.total_files.toLocaleString()}</p>
                    <p className="text-sm text-emerald-600">+{dashboardStats.overview.files_today} today</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <BarChart3 className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-slate-600">Rows Cleaned</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{dashboardStats.overview.total_rows_cleaned.toLocaleString()}</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="w-5 h-5 text-slate-600" />
                      <span className="text-sm text-slate-600">New This Week</span>
                    </div>
                    <p className="text-3xl font-bold text-slate-900">{dashboardStats.overview.new_users_week}</p>
                    <p className="text-sm text-slate-500">signups</p>
                  </div>
                </div>

                {/* Conversion & TTFU */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Percent className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-slate-900">Upload Conversion</span>
                    </div>
                    <p className="text-4xl font-bold text-slate-900 mb-2">{dashboardStats.conversion.upload_rate}%</p>
                    <p className="text-sm text-slate-500">Users who uploaded at least one file</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-slate-900">Paid Conversion</span>
                    </div>
                    <p className="text-4xl font-bold text-slate-900 mb-2">{dashboardStats.conversion.paid_rate}%</p>
                    <p className="text-sm text-slate-500">Users on paid plans</p>
                  </div>
                  
                  <div className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-slate-900">Time to First Upload</span>
                    </div>
                    <p className="text-4xl font-bold text-slate-900 mb-2">
                      {dashboardStats.conversion.avg_time_to_first_upload_hours 
                        ? `${dashboardStats.conversion.avg_time_to_first_upload_hours}h`
                        : 'N/A'}
                    </p>
                    <p className="text-sm text-slate-500">Average time from signup</p>
                  </div>
                </div>

                {/* Plans Distribution */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Users by Plan</h3>
                  <div className="grid grid-cols-4 gap-4">
                    {Object.entries(dashboardStats.plans).map(([plan, count]) => (
                      <div key={plan} className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-2xl font-bold text-slate-900">{count}</p>
                        <p className="text-sm text-slate-600 capitalize">{plan}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Signups Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Daily Signups (Last 30 Days)</h3>
                  <div className="h-48 flex items-end gap-1">
                    {dashboardStats.charts.daily_signups.map((day, idx) => {
                      const maxCount = Math.max(...dashboardStats.charts.daily_signups.map(d => d.count), 1);
                      const height = (day.count / maxCount) * 100;
                      return (
                        <div 
                          key={idx} 
                          className="flex-1 bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors cursor-pointer group relative"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${day.date}: ${day.count} signups`}
                        >
                          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            {day.date}: {day.count}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-xl border border-slate-200">
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Search users..." 
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <span className="text-sm text-slate-500">{filteredUsers.length} users</span>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.slice(0, 50).map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{u.name || 'No name'}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.plan === 'business' ? 'bg-purple-100 text-purple-700' :
                          u.plan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                          u.plan === 'scale' ? 'bg-indigo-100 text-indigo-700' :
                          u.plan === 'pro' ? 'bg-blue-100 text-blue-700' :
                          u.plan === 'standard' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {u.plan}
                        </span>
                      </TableCell>
                      <TableCell>{(u.credits_balance || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleLoginAsUser(u)}
                          disabled={loggingInAs === u.user_id}
                        >
                          {loggingInAs === u.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <LogIn className="w-4 h-4 mr-1" />
                              Login as
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
